import React, { useState, useEffect, useCallback } from 'react';
import ActivityTemplateSelector from './components/ActivityTemplateSelector';
import ActivityConfigurationFlow from './components/ActivityConfigurationFlow';
import { useActivityTemplates } from './hooks/useActivityTemplates';

/**
 * ActivityCreator - Main component for creating curriculum activities
 * Orchestrates the multi-step flow: template selection → configuration → creation
 *
 * Uses shared ActivityConfigurationFlow for steps after template selection,
 * ensuring consistent experience with the kid setup wizard.
 *
 * State Persistence:
 * On old iPhones, switching away from Safari causes page reload.
 * We persist selected template in sessionStorage so users can continue
 * configuration (e.g., entering OTP) after reload.
 */

// Storage key for persisting activity creator state
const getStorageKey = (childId) => `activity_creator_state_${childId}`;

const ActivityCreator = ({
  childId,
  childUsername,
  railsEnv,
  csrfToken,
  onComplete,
  onCancel
}) => {
  const isTestMode = railsEnv === 'test' || railsEnv === 'development';

  // Step management - only need to track template selection here
  // ActivityConfigurationFlow handles all other steps
  const [currentStep, setCurrentStep] = useState('select');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pendingTemplateKey, setPendingTemplateKey] = useState(null);

  // Check sessionStorage for persisted state on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(getStorageKey(childId));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.templateKey) {
          // Store the key to be resolved once templates load
          setPendingTemplateKey(parsed.templateKey);
        }
      }
    } catch (e) {
      // Ignore storage errors
    }
  }, [childId]);

  // Fetch templates
  const { templates, familySubscription, isLoading: templatesLoading, error: templatesError } = useActivityTemplates(childId, csrfToken);

  // Clear persisted state helper
  const clearPersistedState = useCallback(() => {
    try {
      sessionStorage.removeItem(getStorageKey(childId));
    } catch (e) {
      // Ignore storage errors
    }
  }, [childId]);

  // Resolve pending template key once templates are loaded
  useEffect(() => {
    if (pendingTemplateKey && templates && templates.length > 0) {
      const template = templates.find(t => t.key === pendingTemplateKey);
      if (template) {
        setSelectedTemplate(template);
        setCurrentStep('configure');
      } else {
        // Template not found, clear persisted state
        clearPersistedState();
      }
      setPendingTemplateKey(null);
    }
  }, [pendingTemplateKey, templates, clearPersistedState]);

  // Handle template selection
  const handleTemplateSelect = async (template) => {
    setSelectedTemplate(template);
    // Persist selected template for page reload recovery
    try {
      sessionStorage.setItem(getStorageKey(childId), JSON.stringify({ templateKey: template.key }));
    } catch (e) {
      // Ignore storage errors
    }
    // External services use the configuration flow for all steps
    // Non-external services also use the configuration flow which handles
    // immediate creation via initializeFlow
    setCurrentStep('configure');
  };

  // Handle completion from configuration flow
  const handleConfigurationComplete = (result) => {
    // Clear persisted state on successful completion
    clearPersistedState();
    if (onComplete) {
      onComplete(result);
    }
  };

  // Handle back from configuration flow
  const handleBackToTemplates = () => {
    // Clear persisted state when going back to template selection
    clearPersistedState();
    setCurrentStep('select');
    setSelectedTemplate(null);
  };

  // Render loading state (also wait while resolving pending template)
  if (templatesLoading || pendingTemplateKey) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading activity templates...</p>
      </div>
    );
  }

  // Render error state
  if (templatesError) {
    return (
      <div className="alert alert-danger" role="alert">
        <h5 className="alert-heading">Error Loading Templates</h5>
        <p>{templatesError}</p>
        <button className="btn btn-outline-danger" onClick={onCancel}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="activity-creator bg-light min-vh-100" data-activity-creator>
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Step 1: Template Selection */}
            {currentStep === 'select' && (
              <ActivityTemplateSelector
                templates={templates}
                familySubscription={familySubscription}
                onSelect={handleTemplateSelect}
                onCancel={onCancel}
                isTestMode={isTestMode}
              />
            )}

            {/* Steps 2+: Configuration Flow (service account, credentials, subscription, courses) */}
            {currentStep === 'configure' && selectedTemplate && (
              <ActivityConfigurationFlow
                childId={childId}
                childUsername={childUsername}
                template={selectedTemplate}
                csrfToken={csrfToken}
                onComplete={handleConfigurationComplete}
                onBack={handleBackToTemplates}
                isWizardMode={false}
                isTestMode={isTestMode}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityCreator;
